import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { isAbortedOrNetworkRejection } from "@/lib/errors/background-rejection";
import type { RootStore } from "./root.store";
import {
  DEFAULT_NOTIFICATION_FILTERS,
  NOTIFICATION_PAGE_SIZE,
  type TNotification,
  type TNotificationFilters,
  type TNotificationPagination,
  type TNotificationQuery,
  type TNotificationTab,
  type TUnreadNotificationCount,
} from "@/types/notification";

declare global {
  interface Window {
    // Live notification EventSource(s), exposed so E2E tests can await the SSE
    // connection reaching OPEN before asserting real-time delivery.
    __sseStreams?: EventSource[];
  }
}

type TRequestState = "idle" | "loading" | "refreshing" | "loading-more" | "marking-all" | "error";

const EMPTY_UNREAD: TUnreadNotificationCount = {
  total_unread_notifications_count: 0,
  mention_unread_notifications_count: 0,
};
const NOTIFICATION_PREVIEW_SIZE = 6;

// SSE reconnect backoff (issue #1397). A stream that fails to (re)connect — most
// often because the session cookie no longer authenticates — must not be retried
// in a tight loop: every attempt re-hits the notifications stream endpoint, which
// re-authenticates against /api/users/session/ on the backend. We back off
// exponentially with jitter, cap the interval, and after a budget of consecutive
// failures stop retrying until the tab is refocused (when a stale session is most
// likely to have been renewed).
const RECONNECT_BASE_MS = 5_000;
const RECONNECT_MAX_MS = 5 * 60_000;
const RECONNECT_MAX_ATTEMPTS = 10;

/**
 * Another consumer of the workspace's notification stream (NG-2666). The store
 * owns the tab's one EventSource per workspace, and its reconnect and backoff,
 * so a second reader of the same stream registers here rather than opening its
 * own connection.
 */
export interface TStreamListener {
  /** The SSE `event:` name it reads, e.g. `"cache.invalidate"`. */
  readonly event: string;
  /** One event of that name arrived on `workspaceSlug`'s stream; `data` is its raw payload. */
  onMessage(workspaceSlug: string, data: string): void;
  /**
   * `workspaceSlug`'s stream opened again after a drop. Not called on its first
   * open: events published while it was down were never delivered.
   */
  onReopen?(workspaceSlug: string): void;
}

function uniq(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

export class NotificationStore {
  notificationsById = observable.map<string, TNotification>();
  notificationIdsByWorkspaceSlug = observable.map<string, string[]>();
  previewIdsByWorkspaceSlug = observable.map<string, string[]>();
  previewLoadingByWorkspaceSlug = observable.map<string, boolean>();
  unreadCountsByWorkspaceSlug = observable.map<string, TUnreadNotificationCount>();
  paginationByWorkspaceSlug = observable.map<string, TNotificationPagination | null>();
  filtersByWorkspaceSlug = observable.map<string, TNotificationFilters>();
  tabByWorkspaceSlug = observable.map<string, TNotificationTab>();
  /** Notifications with a read, unread, snooze or unsnooze write in flight
   *  (NG-2657, NG-2755). Read, unread and snooze change the row at once,
   *  optimistically, so this is what tells "changed" from "saved": the row is
   *  busy until the server answers. Unsnooze waits for the server instead, and
   *  the row is busy meanwhile. */
  rowSavingIds = observable.set<string>();
  requestState: TRequestState = "idle";
  error: string | null = null;

  private rootStore: RootStore;
  private streamsByWorkspaceSlug = new Map<string, EventSource>();
  private liveRefreshTimersByWorkspaceSlug = new Map<string, number>();
  private reconnectTimersByWorkspaceSlug = new Map<string, number>();
  private reconnectAttemptsByWorkspaceSlug = new Map<string, number>();
  private resumeHandlersByWorkspaceSlug = new Map<string, () => void>();
  private connectedSlugs = new Set<string>();
  // Each registered listener, with the handler it has on each open stream, so
  // removing it detaches it from every one.
  private streamListeners = new Map<TStreamListener, Map<EventSource, EventListener>>();
  // Workspaces whose live refresh was deferred because the tab was hidden when
  // an SSE ping (or reconnect) asked for it — run once the tab is visible (#2756).
  private pendingLiveRefreshSlugs = new Set<string>();
  // The single shared document `visibilitychange` listener that resumes deferred
  // refreshes and aborts in-flight polls; distinct from the per-workspace
  // `armResumeOnVisible` reconnect handler. Held as a ref so it can be removed
  // when the last workspace unsubscribes.
  private visibilityHandler: (() => void) | null = null;
  // AbortControllers for the badge/preview polls currently in flight, so a tab
  // that hides can cancel them before a sleeping laptop strands them (#2756).
  private inFlightPollControllers = new Set<AbortController>();
  // Monotonic per-workspace token for the list query. Every switch of tab or
  // filters bumps it — including the one `revealNotification` makes for a deep
  // link — so a response belonging to the superseded query is dropped instead of
  // overwriting the newer page. Without it the pane's mount-time load and a deep
  // link's re-load race, and whichever lands last wins.
  private listSeqByWorkspaceSlug = new Map<string, number>();
  // One fetch-by-id per deep-linked notification, kept for the session. A
  // resolved entry doubles as the "already tried" record — including for a 404,
  // where retrying on every re-render would loop a request the API will keep
  // refusing.
  private fetchByIdPromises = new Map<string, Promise<TNotification | undefined>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        streamsByWorkspaceSlug: false,
        liveRefreshTimersByWorkspaceSlug: false,
        reconnectTimersByWorkspaceSlug: false,
        reconnectAttemptsByWorkspaceSlug: false,
        resumeHandlersByWorkspaceSlug: false,
        connectedSlugs: false,
        streamListeners: false,
        pendingLiveRefreshSlugs: false,
        visibilityHandler: false,
        inFlightPollControllers: false,
        listSeqByWorkspaceSlug: false,
        fetchByIdPromises: false,
      } as never,
      { autoBind: true },
    );
  }

  getUnreadCount(workspaceSlug: string): TUnreadNotificationCount {
    return this.unreadCountsByWorkspaceSlug.get(workspaceSlug) ?? EMPTY_UNREAD;
  }

  getCurrentTab(workspaceSlug: string): TNotificationTab {
    return this.tabByWorkspaceSlug.get(workspaceSlug) ?? "all";
  }

  getCurrentFilters(workspaceSlug: string): TNotificationFilters {
    return this.filtersByWorkspaceSlug.get(workspaceSlug) ?? DEFAULT_NOTIFICATION_FILTERS;
  }

  getPagination(workspaceSlug: string): TNotificationPagination | null {
    return this.paginationByWorkspaceSlug.get(workspaceSlug) ?? null;
  }

  getNotificationById(id: string): TNotification | undefined {
    return this.notificationsById.get(id);
  }

  /** The bell popover's rows: the newest active notifications, read and unread
   *  alike (NG-2539). A notification the user just read stays listed and is
   *  shown as read — dropping it made it look deleted. Archived and snoozed
   *  rows are still excluded: the list endpoint already filters them out, but
   *  the preview can hold one from before it was archived or snoozed. */
  getPreviewNotifications(workspaceSlug: string): TNotification[] {
    const ids = this.previewIdsByWorkspaceSlug.get(workspaceSlug) ?? [];
    return ids
      .map((id) => this.notificationsById.get(id))
      .filter((n): n is TNotification => Boolean(n))
      .filter((n) => !n.archived_at && !n.snoozed_till);
  }

  isRowSaving(notificationId: string): boolean {
    return this.rowSavingIds.has(notificationId);
  }

  isPreviewLoading(workspaceSlug: string): boolean {
    return this.previewLoadingByWorkspaceSlug.get(workspaceSlug) ?? false;
  }

  getVisibleNotifications(workspaceSlug: string): TNotification[] {
    const ids = this.notificationIdsByWorkspaceSlug.get(workspaceSlug) ?? [];
    const tab = this.getCurrentTab(workspaceSlug);
    const filters = this.getCurrentFilters(workspaceSlug);
    return ids
      .map((id) => this.notificationsById.get(id))
      .filter((n): n is TNotification => Boolean(n))
      .filter((n) =>
        tab === "mentions" ? n.is_mentioned_notification : !n.is_mentioned_notification,
      )
      .filter((n) => {
        if (filters.view === "archived") return Boolean(n.archived_at);
        if (filters.view === "snoozed") return Boolean(n.snoozed_till);
        return !n.archived_at && !n.snoozed_till;
      })
      .filter((n) => (filters.unreadOnly ? n.read_at === null : true));
  }

  buildQuery(workspaceSlug: string, cursor?: string): TNotificationQuery {
    const tab = this.getCurrentTab(workspaceSlug);
    const filters = this.getCurrentFilters(workspaceSlug);
    const query: TNotificationQuery = {
      per_page: NOTIFICATION_PAGE_SIZE,
    };
    if (filters.view === "archived") query.archived = true;
    if (filters.view === "snoozed") query.snoozed = true;
    if (filters.unreadOnly) query.read = false;
    if (tab === "mentions") query.mentioned = true;
    if (cursor) query.cursor = cursor;
    return query;
  }

  private currentListSeq(workspaceSlug: string): number {
    return this.listSeqByWorkspaceSlug.get(workspaceSlug) ?? 0;
  }

  /** Invalidate the in-flight list query for this workspace; see listSeqByWorkspaceSlug. */
  private bumpListSeq(workspaceSlug: string): void {
    this.listSeqByWorkspaceSlug.set(workspaceSlug, this.currentListSeq(workspaceSlug) + 1);
  }

  /** True once a newer tab/filter query has superseded the one that started at
   * `seq` — its response owns none of the shared list state any more. */
  private isSupersededList(workspaceSlug: string, seq: number): boolean {
    return seq !== this.currentListSeq(workspaceSlug);
  }

  /** Whether a list read for `workspaceSlug` has landed (NG-2128). The pane loads in
   *  an effect, after the first paint, and `requestState` starts at "idle", so before
   *  this the pane drew its empty state for a frame on every first open. */
  hasLoadedList(workspaceSlug: string): boolean {
    return this.notificationIdsByWorkspaceSlug.has(workspaceSlug);
  }

  private ensureWorkspace(workspaceSlug: string): void {
    if (!this.filtersByWorkspaceSlug.has(workspaceSlug)) {
      this.filtersByWorkspaceSlug.set(workspaceSlug, { ...DEFAULT_NOTIFICATION_FILTERS });
    }
    if (!this.tabByWorkspaceSlug.has(workspaceSlug)) {
      this.tabByWorkspaceSlug.set(workspaceSlug, "all");
    }
  }

  async fetchUnreadCount(workspaceSlug: string): Promise<TUnreadNotificationCount | undefined> {
    const controller = this.trackPollController();
    try {
      const unread = await this.rootStore.notificationService.getUnreadCount(workspaceSlug, {
        signal: controller.signal,
      });
      runInAction(() => {
        this.unreadCountsByWorkspaceSlug.set(workspaceSlug, unread);
      });
      return unread;
    } catch (err) {
      // Badge failures are non-fatal. A tab-hide abort / network drop (#2756) is
      // an intended cancellation, not a fault — stay silent on those; log the
      // rest as expected degradation.
      if (!isAbortedOrNetworkRejection(err)) {
        console.warn("[notifications] unread count poll failed", { workspaceSlug }, err);
      }
      return undefined;
    } finally {
      this.releasePollController(controller);
    }
  }

  subscribe(workspaceSlug: string): void {
    if (this.streamsByWorkspaceSlug.has(workspaceSlug)) return;

    // A fresh subscribe supersedes any pending reconnect timer or paused-resume
    // listener for this workspace.
    this.clearReconnectTimer(workspaceSlug);
    this.clearResumeHandler(workspaceSlug);

    const stream = this.rootStore.notificationService.openStream(workspaceSlug);
    if (!stream) return;

    // A live workspace needs the shared visibility listener so a hidden-tab
    // refresh can be deferred and resumed on refocus (#2756).
    this.ensureVisibilityHandler();

    stream.onopen = () => {
      // A healthy (re)connection clears the backoff budget so the next drop
      // starts over from the base delay.
      this.reconnectAttemptsByWorkspaceSlug.delete(workspaceSlug);
      if (this.connectedSlugs.has(workspaceSlug)) {
        // The SSE stream just reconnected after a drop (network blip, tab sleep, etc.).
        // Redis pub/sub has no persistence — any "notification.created" pings published
        // while the stream was down were never delivered. The notifications themselves are
        // safe in Postgres, but the browser won't know about them until it asks.
        // A fresh page load already handles this via loadInitial; this covers the case
        // where the tab stayed open and the stream quietly reconnected without a new
        // event arriving to trigger a refresh.
        this.scheduleLiveRefresh(workspaceSlug);
        for (const listener of this.streamListeners.keys()) {
          this.notifyListener(listener, workspaceSlug, "reopen", () =>
            listener.onReopen?.(workspaceSlug),
          );
        }
      }
      this.connectedSlugs.add(workspaceSlug);
    };
    stream.addEventListener("notification", () => {
      this.scheduleLiveRefresh(workspaceSlug);
    });
    for (const listener of this.streamListeners.keys()) {
      this.attachListener(stream, workspaceSlug, listener);
    }
    stream.onerror = () => {
      this.closeStream(workspaceSlug);
      this.scheduleReconnect(workspaceSlug);
    };
    this.streamsByWorkspaceSlug.set(workspaceSlug, stream);
    (window.__sseStreams ??= []).push(stream);
  }

  /**
   * Read another event type off this tab's notification streams, the open ones
   * and every one opened later. Returns the function that stops it.
   */
  addStreamListener(listener: TStreamListener): () => void {
    if (!this.streamListeners.has(listener)) {
      this.streamListeners.set(listener, new Map());
      for (const [workspaceSlug, stream] of this.streamsByWorkspaceSlug) {
        this.attachListener(stream, workspaceSlug, listener);
      }
    }
    return () => {
      const handlers = this.streamListeners.get(listener);
      if (!handlers) return;
      for (const [stream, handler] of handlers) {
        stream.removeEventListener(listener.event, handler);
      }
      this.streamListeners.delete(listener);
    };
  }

  private attachListener(stream: EventSource, workspaceSlug: string, listener: TStreamListener) {
    const handler: EventListener = (event) => {
      const data = (event as MessageEvent<string>).data;
      this.notifyListener(listener, workspaceSlug, "message", () =>
        listener.onMessage(workspaceSlug, data),
      );
    };
    stream.addEventListener(listener.event, handler);
    this.streamListeners.get(listener)?.set(stream, handler);
  }

  // A failing listener must not cost the notification stream its own handling.
  private notifyListener(
    listener: TStreamListener,
    workspaceSlug: string,
    kind: "message" | "reopen",
    run: () => void,
  ): void {
    try {
      run();
    } catch (error) {
      console.error(
        "notification_stream_listener_failed",
        { workspaceSlug, event: listener.event, kind },
        error,
      );
    }
  }

  private scheduleReconnect(workspaceSlug: string): void {
    const attempts = this.reconnectAttemptsByWorkspaceSlug.get(workspaceSlug) ?? 0;

    if (attempts >= RECONNECT_MAX_ATTEMPTS) {
      // Persistent failure (most often an expired/invalid session). Stop the
      // reconnect loop so we don't keep re-authenticating against the backend;
      // resume once when the tab is next focused, where a stale session is most
      // likely to have been renewed.
      console.warn("notification_stream_reconnect_exhausted", { workspaceSlug, attempts });
      this.armResumeOnVisible(workspaceSlug);
      return;
    }

    const ceiling = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempts);
    // Full jitter over [ceiling/2, ceiling] so many tabs don't reconnect in
    // lockstep and stampede the backend after a shared outage.
    const delay = ceiling / 2 + Math.random() * (ceiling / 2);
    this.reconnectAttemptsByWorkspaceSlug.set(workspaceSlug, attempts + 1);

    const timer = window.setTimeout(() => {
      this.reconnectTimersByWorkspaceSlug.delete(workspaceSlug);
      this.subscribe(workspaceSlug);
    }, delay);
    this.reconnectTimersByWorkspaceSlug.set(workspaceSlug, timer);
  }

  private armResumeOnVisible(workspaceSlug: string): void {
    if (typeof document === "undefined") return;
    if (this.resumeHandlersByWorkspaceSlug.has(workspaceSlug)) return;

    const handler = () => {
      if (document.visibilityState !== "visible") return;
      this.clearResumeHandler(workspaceSlug);
      // Hand the refocused tab a fresh backoff budget for its retry.
      this.reconnectAttemptsByWorkspaceSlug.delete(workspaceSlug);
      this.subscribe(workspaceSlug);
    };
    this.resumeHandlersByWorkspaceSlug.set(workspaceSlug, handler);
    document.addEventListener("visibilitychange", handler);
  }

  private clearReconnectTimer(workspaceSlug: string): void {
    const timer = this.reconnectTimersByWorkspaceSlug.get(workspaceSlug);
    if (timer) window.clearTimeout(timer);
    this.reconnectTimersByWorkspaceSlug.delete(workspaceSlug);
  }

  private clearResumeHandler(workspaceSlug: string): void {
    const handler = this.resumeHandlersByWorkspaceSlug.get(workspaceSlug);
    if (handler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handler);
    }
    this.resumeHandlersByWorkspaceSlug.delete(workspaceSlug);
  }

  unsubscribe(workspaceSlug: string): void {
    this.closeStream(workspaceSlug);

    const timer = this.liveRefreshTimersByWorkspaceSlug.get(workspaceSlug);
    if (timer) window.clearTimeout(timer);
    this.liveRefreshTimersByWorkspaceSlug.delete(workspaceSlug);

    this.clearReconnectTimer(workspaceSlug);
    this.clearResumeHandler(workspaceSlug);
    this.reconnectAttemptsByWorkspaceSlug.delete(workspaceSlug);
    this.connectedSlugs.delete(workspaceSlug);
    this.pendingLiveRefreshSlugs.delete(workspaceSlug);

    // Don't leak the shared visibility listener: drop it once nothing is
    // subscribed (the store is a singleton, but tests create many).
    if (this.streamsByWorkspaceSlug.size === 0) {
      this.teardownVisibilityHandler();
    }
  }

  private closeStream(workspaceSlug: string): void {
    const stream = this.streamsByWorkspaceSlug.get(workspaceSlug);
    stream?.close();
    this.streamsByWorkspaceSlug.delete(workspaceSlug);
    if (!stream) return;
    for (const handlers of this.streamListeners.values()) handlers.delete(stream);
  }

  private scheduleLiveRefresh(workspaceSlug: string): void {
    // On a hidden tab the refresh GETs are wasted work and — worse — are exactly
    // the requests a sleeping laptop strands in flight, surfacing on wake as
    // Sentry transport noise (#2756). Defer to when the tab is next visible and
    // run it then via the shared visibility listener.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.deferLiveRefresh(workspaceSlug);
      return;
    }

    const existing = this.liveRefreshTimersByWorkspaceSlug.get(workspaceSlug);
    if (existing) window.clearTimeout(existing);

    const timer = window.setTimeout(() => {
      this.liveRefreshTimersByWorkspaceSlug.delete(workspaceSlug);
      this.runLiveRefresh(workspaceSlug);
    }, 250);

    this.liveRefreshTimersByWorkspaceSlug.set(workspaceSlug, timer);
  }

  // Record a workspace as pending and arm the shared visibility listener, so the
  // refresh runs when the tab is next visible. Cancels any debounce timer already
  // scheduled (while visible) for this workspace, so becoming visible fires the
  // deferred run once rather than also firing the stale timer (#2756).
  private deferLiveRefresh(workspaceSlug: string): void {
    const existing = this.liveRefreshTimersByWorkspaceSlug.get(workspaceSlug);
    if (existing) window.clearTimeout(existing);
    this.liveRefreshTimersByWorkspaceSlug.delete(workspaceSlug);

    this.pendingLiveRefreshSlugs.add(workspaceSlug);
    this.ensureVisibilityHandler();
  }

  // The refresh body shared by the debounce timer and the visibility-resume path.
  private runLiveRefresh(workspaceSlug: string): void {
    // Visibility is re-checked here, not only at schedule time: a refresh armed
    // while visible can reach this debounce callback after the tab has hidden.
    // Re-defer rather than fire the wasted, strandable GETs (#2756).
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.deferLiveRefresh(workspaceSlug);
      return;
    }
    void this.fetchUnreadCount(workspaceSlug);
    void this.loadPreview(workspaceSlug);
    if (this.notificationIdsByWorkspaceSlug.has(workspaceSlug) && this.requestState === "idle") {
      void this.refresh(workspaceSlug).catch((error) => {
        console.error("notification_live_refresh_failed", error);
      });
    }
  }

  // Run every refresh deferred while the tab was hidden, once, on refocus (#2756).
  private flushPendingLiveRefreshes(): void {
    if (this.pendingLiveRefreshSlugs.size === 0) return;
    const slugs = Array.from(this.pendingLiveRefreshSlugs);
    this.pendingLiveRefreshSlugs.clear();
    for (const slug of slugs) {
      this.runLiveRefresh(slug);
    }
  }

  // Registers a controller for an in-flight badge/preview poll and makes sure the
  // shared visibility listener is armed to abort it if the tab hides (#2756).
  private trackPollController(): AbortController {
    const controller = new AbortController();
    this.inFlightPollControllers.add(controller);
    this.ensureVisibilityHandler();
    return controller;
  }

  private releasePollController(controller: AbortController): void {
    this.inFlightPollControllers.delete(controller);
  }

  // Cancel every in-flight badge/preview poll — a hidden tab must not leave a
  // GET in flight for a sleeping laptop to strand (#2756).
  private abortInFlightPolls(): void {
    for (const controller of this.inFlightPollControllers) {
      controller.abort();
    }
    this.inFlightPollControllers.clear();
  }

  // Lazily arm the single shared `visibilitychange` listener (idempotent). Kept
  // separate from the per-workspace `armResumeOnVisible` reconnect handler.
  private ensureVisibilityHandler(): void {
    if (typeof document === "undefined") return;
    if (this.visibilityHandler) return;

    const handler = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "hidden") {
        this.abortInFlightPolls();
      } else {
        this.flushPendingLiveRefreshes();
      }
    };
    this.visibilityHandler = handler;
    document.addEventListener("visibilitychange", handler);
  }

  private teardownVisibilityHandler(): void {
    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    this.visibilityHandler = null;
  }

  setTab(workspaceSlug: string, tab: TNotificationTab): void {
    this.ensureWorkspace(workspaceSlug);
    this.bumpListSeq(workspaceSlug);
    this.tabByWorkspaceSlug.set(workspaceSlug, tab);
    this.notificationIdsByWorkspaceSlug.set(workspaceSlug, []);
    this.paginationByWorkspaceSlug.set(workspaceSlug, null);
    void this.loadInitial(workspaceSlug);
  }

  setFilters(workspaceSlug: string, update: Partial<TNotificationFilters>): void {
    this.ensureWorkspace(workspaceSlug);
    this.bumpListSeq(workspaceSlug);
    const current = this.getCurrentFilters(workspaceSlug);
    this.filtersByWorkspaceSlug.set(workspaceSlug, {
      ...current,
      ...update,
    });
    this.notificationIdsByWorkspaceSlug.set(workspaceSlug, []);
    this.paginationByWorkspaceSlug.set(workspaceSlug, null);
    void this.loadInitial(workspaceSlug);
  }

  /**
   * Align the list query with the notification a deep link (`?notificationId=`)
   * selected, so the list pane has a row for it.
   *
   * Three ways a selected notification is otherwise unreachable — the first two
   * of them straight from the bell, whose popover previews the newest
   * notifications of every kind, read and unread (NG-2539), with no regard for
   * the list's query:
   *   1. It is filtered out. The "all" tab means "not mentions"
   *      (`getVisibleNotifications`), and the active view excludes archived and
   *      snoozed rows — so a mention opened from the bell used to land on a list
   *      that could never render it, detail pane open beside an absent row.
   *   2. It is past the first page. The preview is capped at
   *      NOTIFICATION_PREVIEW_SIZE rows while the list is one
   *      NOTIFICATION_PAGE_SIZE page of every status, so a busy member can
   *      preview a row the list's first page doesn't reach.
   *   3. No fetch has seen it at all — a pasted URL, where the detail pane had
   *      nothing to render either. `fetchNotification` loads it by id.
   *
   * Callers rely on this being idempotent: selecting a row already in the
   * current tab/view is a no-op, so it is safe to run on every selection. A
   * notification no fetch has seen yet is loaded by id first (see
   * `fetchNotification`); one the API won't produce is left alone.
   */
  async revealNotification(workspaceSlug: string, notificationId: string): Promise<void> {
    const target = await this.fetchNotification(workspaceSlug, notificationId);
    if (!target) return;
    this.ensureWorkspace(workspaceSlug);

    const tab: TNotificationTab = target.is_mentioned_notification ? "mentions" : "all";
    const view: TNotificationFilters["view"] = target.archived_at
      ? "archived"
      : target.snoozed_till
        ? "snoozed"
        : "active";
    const filters = this.getCurrentFilters(workspaceSlug);

    if (this.getCurrentTab(workspaceSlug) !== tab || filters.view !== view) {
      this.bumpListSeq(workspaceSlug);
      runInAction(() => {
        this.tabByWorkspaceSlug.set(workspaceSlug, tab);
        this.filtersByWorkspaceSlug.set(workspaceSlug, { ...filters, view });
        this.notificationIdsByWorkspaceSlug.set(workspaceSlug, []);
        this.paginationByWorkspaceSlug.set(workspaceSlug, null);
      });
      // A failed reload surfaces the pane's retry UI; the splice below still
      // gives the selected notification a row to render in.
      await this.loadInitial(workspaceSlug).catch(() => {});
    }

    const ids = this.notificationIdsByWorkspaceSlug.get(workspaceSlug) ?? [];
    if (ids.includes(notificationId)) return;
    runInAction(() => {
      this.notificationIdsByWorkspaceSlug.set(
        workspaceSlug,
        this.insertNewestFirst(ids, notificationId),
      );
    });
  }

  /**
   * Load one notification by id, for a deep link whose target no list or preview
   * fetch has seen — a pasted URL, or a bell click after a reload. Resolves to
   * the notification (from the store when it is already there) or to undefined
   * when the API won't produce it, which for a receiver-scoped 404 is the
   * correct end state, not an error to surface.
   *
   * Concurrent callers share one request, and the settled promise is kept so a
   * re-render can't turn a missing row into a request loop.
   */
  async fetchNotification(
    workspaceSlug: string,
    notificationId: string,
  ): Promise<TNotification | undefined> {
    const cached = this.notificationsById.get(notificationId);
    if (cached) return cached;

    const inFlight = this.fetchByIdPromises.get(notificationId);
    if (inFlight) return inFlight;

    const request = this.rootStore.notificationService
      .retrieve(workspaceSlug, notificationId)
      .then((notification) => {
        runInAction(() => {
          this.notificationsById.set(notification.id, notification);
        });
        return notification;
      })
      .catch((error: unknown) => {
        // Expected degradation: the detail pane falls back to its placeholder.
        console.warn(
          "[notifications] deep-linked notification could not be loaded",
          { workspaceSlug, notificationId },
          error,
        );
        return undefined;
      });
    this.fetchByIdPromises.set(notificationId, request);
    return request;
  }

  /** Insert `id` into a newest-first id list by `created_at`, keeping that order
   * — the list pane renders ids in array order, so appending would show an old
   * notification above newer ones. */
  private insertNewestFirst(ids: string[], id: string): string[] {
    const target = this.notificationsById.get(id);
    const createdAt = target?.created_at ?? "";
    const at = ids.findIndex((existing) => {
      const other = this.notificationsById.get(existing);
      return (other?.created_at ?? "") <= createdAt;
    });
    return at === -1 ? [...ids, id] : [...ids.slice(0, at), id, ...ids.slice(at)];
  }

  async loadInitial(workspaceSlug: string): Promise<void> {
    this.ensureWorkspace(workspaceSlug);
    const seq = this.currentListSeq(workspaceSlug);
    this.requestState = "loading";
    this.error = null;
    try {
      await this.fetchUnreadCount(workspaceSlug);
      const response = await this.rootStore.notificationService.list(
        workspaceSlug,
        this.buildQuery(workspaceSlug),
      );
      if (this.isSupersededList(workspaceSlug, seq)) return;
      runInAction(() => {
        response.results.forEach((n) => {
          this.notificationsById.set(n.id, n);
        });
        this.notificationIdsByWorkspaceSlug.set(
          workspaceSlug,
          response.results.map((n) => n.id),
        );
        this.paginationByWorkspaceSlug.set(workspaceSlug, {
          next_cursor: response.next_cursor,
          prev_cursor: response.prev_cursor,
          next_page_results: response.next_page_results,
          total_results: response.total_results,
        });
        this.requestState = "idle";
      });
    } catch (error) {
      // Let the newer query report its own outcome rather than flipping the
      // pane into an error on behalf of one nobody is waiting for.
      if (this.isSupersededList(workspaceSlug, seq)) return;
      runInAction(() => {
        this.requestState = "error";
        this.error = "Failed to load notifications";
      });
      throw error;
    }
  }

  async loadPreview(workspaceSlug: string): Promise<void> {
    this.previewLoadingByWorkspaceSlug.set(workspaceSlug, true);
    const controller = this.trackPollController();
    try {
      // The newest active notifications, read and unread (NG-2539) — the
      // endpoint already excludes archived and snoozed rows unless asked for
      // them, so this is the same window the full page's "active" view loads,
      // just capped at the popover's size. Asking for `read: false` here made
      // the bell an unread-only inbox, which is how a notification appeared to
      // vanish the moment it was read.
      const response = await this.rootStore.notificationService.list(
        workspaceSlug,
        { per_page: NOTIFICATION_PREVIEW_SIZE },
        { signal: controller.signal },
      );
      runInAction(() => {
        response.results.forEach((n) => {
          this.notificationsById.set(n.id, n);
        });
        this.previewIdsByWorkspaceSlug.set(
          workspaceSlug,
          response.results.map((n) => n.id),
        );
        this.previewLoadingByWorkspaceSlug.set(workspaceSlug, false);
      });
    } catch (err) {
      // Clears the loading flag on any failure; the next visible refresh reloads
      // the preview. A tab-hide abort / network drop (#2756) is an intended
      // cancellation — stay silent on those; log the rest as expected degradation.
      if (!isAbortedOrNetworkRejection(err)) {
        console.warn("[notifications] preview load failed", { workspaceSlug }, err);
      }
      runInAction(() => {
        this.previewLoadingByWorkspaceSlug.set(workspaceSlug, false);
      });
    } finally {
      this.releasePollController(controller);
    }
  }

  async refresh(workspaceSlug: string): Promise<void> {
    const seq = this.currentListSeq(workspaceSlug);
    this.requestState = "refreshing";
    try {
      await this.fetchUnreadCount(workspaceSlug);
      const response = await this.rootStore.notificationService.list(
        workspaceSlug,
        this.buildQuery(workspaceSlug),
      );
      if (this.isSupersededList(workspaceSlug, seq)) return;
      runInAction(() => {
        response.results.forEach((n) => {
          this.notificationsById.set(n.id, n);
        });
        this.notificationIdsByWorkspaceSlug.set(
          workspaceSlug,
          response.results.map((n) => n.id),
        );
        this.paginationByWorkspaceSlug.set(workspaceSlug, {
          next_cursor: response.next_cursor,
          prev_cursor: response.prev_cursor,
          next_page_results: response.next_page_results,
          total_results: response.total_results,
        });
        this.requestState = "idle";
      });
    } catch (error) {
      if (this.isSupersededList(workspaceSlug, seq)) return;
      runInAction(() => {
        this.requestState = "error";
      });
      throw error;
    }
  }

  async loadMore(workspaceSlug: string): Promise<void> {
    const pagination = this.getPagination(workspaceSlug);
    if (!pagination?.next_cursor) return;
    const seq = this.currentListSeq(workspaceSlug);
    this.requestState = "loading-more";
    try {
      const response = await this.rootStore.notificationService.list(
        workspaceSlug,
        this.buildQuery(workspaceSlug, pagination.next_cursor),
      );
      // Appending a superseded page would splice the previous tab's rows into
      // the current one.
      if (this.isSupersededList(workspaceSlug, seq)) return;
      runInAction(() => {
        response.results.forEach((n) => {
          this.notificationsById.set(n.id, n);
        });
        const existing = this.notificationIdsByWorkspaceSlug.get(workspaceSlug) ?? [];
        this.notificationIdsByWorkspaceSlug.set(
          workspaceSlug,
          uniq([...existing, ...response.results.map((n) => n.id)]),
        );
        this.paginationByWorkspaceSlug.set(workspaceSlug, {
          next_cursor: response.next_cursor,
          prev_cursor: response.prev_cursor,
          next_page_results: response.next_page_results,
          total_results: response.total_results,
        });
        this.requestState = "idle";
      });
    } catch (error) {
      if (this.isSupersededList(workspaceSlug, seq)) return;
      runInAction(() => {
        this.requestState = "error";
      });
      throw error;
    }
  }

  private adjustUnread(workspaceSlug: string, isMention: boolean, delta: number): void {
    const current = this.getUnreadCount(workspaceSlug);
    const key = isMention
      ? "mention_unread_notifications_count"
      : "total_unread_notifications_count";
    this.unreadCountsByWorkspaceSlug.set(workspaceSlug, {
      ...current,
      [key]: Math.max(0, current[key] + delta),
    });
  }

  async markAsRead(
    workspaceSlug: string,
    notificationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.notificationsById.get(notificationId);
    if (!current || current.read_at) return;
    const backup = { ...current };
    const isMention = Boolean(current.is_mentioned_notification);
    runInAction(() => {
      // The row stays in the bell preview: since NG-2539 the preview lists
      // read and unread rows, so a read notification is shown as read rather
      // than dropped (which made it look deleted).
      this.notificationsById.set(notificationId, {
        ...current,
        read_at: new Date().toISOString(),
      });
      this.adjustUnread(workspaceSlug, isMention, -1);
      this.rowSavingIds.add(notificationId);
    });
    try {
      // The mark-read endpoint returns 204 (no body); keep the optimistic
      // read_at rather than overwriting the row with an empty response (which
      // would blank the notification until a refetch).
      await this.rootStore.notificationService.markAsRead(workspaceSlug, notificationId);
      track("notification_read", { notification_id: notificationId, source });
    } catch (error) {
      runInAction(() => {
        this.notificationsById.set(notificationId, backup);
        this.adjustUnread(workspaceSlug, isMention, 1);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rowSavingIds.delete(notificationId);
      });
    }
  }

  async markAsUnread(
    workspaceSlug: string,
    notificationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.notificationsById.get(notificationId);
    if (!current || !current.read_at) return;
    const backup = { ...current };
    const isMention = Boolean(current.is_mentioned_notification);
    runInAction(() => {
      // No preview bookkeeping: the preview lists read and unread rows
      // (NG-2539), so the row is still there and the read_at flip is enough.
      this.notificationsById.set(notificationId, { ...current, read_at: null });
      this.adjustUnread(workspaceSlug, isMention, 1);
      this.rowSavingIds.add(notificationId);
    });
    try {
      // 204 (no body) — keep the optimistic read_at: null rather than
      // overwriting the row with an empty response.
      await this.rootStore.notificationService.markAsUnread(workspaceSlug, notificationId);
      track("notification_unread", { notification_id: notificationId, source });
    } catch (error) {
      runInAction(() => {
        this.notificationsById.set(notificationId, backup);
        this.adjustUnread(workspaceSlug, isMention, -1);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rowSavingIds.delete(notificationId);
      });
    }
  }

  async archive(
    workspaceSlug: string,
    notificationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.notificationsById.get(notificationId);
    if (!current) return;
    const backup = { ...current };
    const isMention = Boolean(current.is_mentioned_notification);
    const wasUnread = current.read_at === null;
    runInAction(() => {
      this.notificationsById.set(notificationId, {
        ...current,
        archived_at: new Date().toISOString(),
      });
      if (wasUnread) this.adjustUnread(workspaceSlug, isMention, -1);
    });
    try {
      // 204 (no body) — keep the optimistic archived_at rather than
      // overwriting the row with an empty response.
      await this.rootStore.notificationService.archive(workspaceSlug, notificationId);
      track("notification_archived", { notification_id: notificationId, source });
    } catch (error) {
      runInAction(() => {
        this.notificationsById.set(notificationId, backup);
        if (wasUnread) this.adjustUnread(workspaceSlug, isMention, 1);
      });
      throw error;
    }
  }

  async unarchive(
    workspaceSlug: string,
    notificationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.notificationsById.get(notificationId);
    if (!current) return;
    const backup = { ...current };
    const isMention = Boolean(current.is_mentioned_notification);
    const wasArchivedUnread = Boolean(current.archived_at) && current.read_at === null;
    runInAction(() => {
      this.notificationsById.set(notificationId, { ...current, archived_at: null });
      if (wasArchivedUnread) this.adjustUnread(workspaceSlug, isMention, 1);
    });
    try {
      // 204 (no body) — keep the optimistic archived_at: null rather than
      // overwriting the row with an empty response.
      await this.rootStore.notificationService.unarchive(workspaceSlug, notificationId);
      track("notification_unarchived", { notification_id: notificationId, source });
    } catch (error) {
      runInAction(() => {
        this.notificationsById.set(notificationId, backup);
        if (wasArchivedUnread) this.adjustUnread(workspaceSlug, isMention, -1);
      });
      throw error;
    }
  }

  async snooze(
    workspaceSlug: string,
    notificationId: string,
    snoozedTill: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.notificationsById.get(notificationId);
    if (!current) return;
    const backup = { ...current };
    const isMention = Boolean(current.is_mentioned_notification);
    const shouldAdjust = current.read_at === null && !current.snoozed_till;
    runInAction(() => {
      this.notificationsById.set(notificationId, { ...current, snoozed_till: snoozedTill });
      if (shouldAdjust) this.adjustUnread(workspaceSlug, isMention, -1);
      this.rowSavingIds.add(notificationId);
    });
    try {
      const updated = await this.rootStore.notificationService.update(
        workspaceSlug,
        notificationId,
        { snoozed_till: snoozedTill },
      );
      runInAction(() => {
        this.notificationsById.set(notificationId, updated);
      });
      track("notification_snoozed", { notification_id: notificationId, source });
    } catch (error) {
      runInAction(() => {
        this.notificationsById.set(notificationId, backup);
        if (shouldAdjust) this.adjustUnread(workspaceSlug, isMention, 1);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rowSavingIds.delete(notificationId);
      });
    }
  }

  async unsnooze(
    workspaceSlug: string,
    notificationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.notificationsById.get(notificationId);
    if (!current) return;
    const isMention = Boolean(current.is_mentioned_notification);
    const shouldAdjust = current.read_at === null && Boolean(current.snoozed_till);
    // Not optimistic (NG-2755): clearing snoozed_till at once took the row out
    // of the Snoozed view before the server answered, so nothing showed the
    // write in flight. The row stays, busy, until the PATCH answers, and only
    // then leaves the view, which is the user's signal that it saved.
    runInAction(() => {
      this.rowSavingIds.add(notificationId);
    });
    try {
      const updated = await this.rootStore.notificationService.update(
        workspaceSlug,
        notificationId,
        { snoozed_till: null },
      );
      runInAction(() => {
        this.notificationsById.set(notificationId, updated);
        if (shouldAdjust) this.adjustUnread(workspaceSlug, isMention, 1);
      });
      track("notification_unsnoozed", { notification_id: notificationId, source });
    } finally {
      runInAction(() => {
        this.rowSavingIds.delete(notificationId);
      });
    }
  }

  async markAllAsRead(workspaceSlug: string, source: AnalyticsSource = "page"): Promise<void> {
    const tab = this.getCurrentTab(workspaceSlug);
    const filters = this.getCurrentFilters(workspaceSlug);
    this.requestState = "marking-all";
    try {
      // Mentions tab: backend mark-all-read endpoint has no `mentioned` filter, so we
      // fall back to per-row read mutations for currently loaded mention rows. Mention
      // rows that haven't been paginated yet stay unread until they are loaded.
      if (tab === "mentions") {
        const unreadIds = (this.notificationIdsByWorkspaceSlug.get(workspaceSlug) ?? []).filter(
          (id) => this.notificationsById.get(id)?.read_at === null,
        );
        await Promise.all(
          unreadIds.map((id) => this.rootStore.notificationService.markAsRead(workspaceSlug, id)),
        );
      } else {
        await this.rootStore.notificationService.markAllAsRead(workspaceSlug, {
          type: "all",
          snoozed: filters.view === "snoozed",
          archived: filters.view === "archived",
          read: false,
        });
      }
      // Before the re-reads, so a failed refresh can't drop it.
      track("notifications_all_read", { tab, view: filters.view, source });
      await this.fetchUnreadCount(workspaceSlug);
      await this.loadPreview(workspaceSlug);
      await this.refresh(workspaceSlug);
    } finally {
      runInAction(() => {
        if (this.requestState === "marking-all") this.requestState = "idle";
      });
    }
  }
}
